<script>
    import app from "@/store/app";
    import Icon from "@iconify/svelte";
    import { mq } from "@/assets/components/MediaQuery.svelte";
    import { push } from "@/assets/js/util";
    import { fly, fade } from "svelte/transition";
    import { cubicOut } from "svelte/easing";
    import { routes, routesAdmin } from "@/assets/js/options";
    import { onMount, onDestroy } from "svelte";
    import { listVisibleTenantCategories } from "@/assets/js/categoryInventory.js";
    import tenant from "@/store/tenant.js";
    import { isInTenantContext } from "@/assets/js/tenant";

    let categories = [];
    let drawer;
    let isAdmin = false;
    let items = [];

    onMount(async () => {
        const url = new URL(window.location);
        isAdmin = url.pathname.split("/")[1] === "admin";
        if (isAdmin) {
            items = routesAdmin;
        } else if (isInTenantContext()) {
            items = [];
        } else {
            items = routes;
        }
        if (isInTenantContext() && $tenant.id) {
            try {
                await listVisibleTenantCategories($tenant.id);
            } catch (e) {
            }
        }
    });

    // Best-effort icon for a storefront category so drawer rows match the
    // "cd" thumb style. Falls back to a generic tag for unknown categories.
    const catIcon = (name = "") => {
        const n = name.toLowerCase();
        if (n.includes("flower")) return "mdi:cannabis";
        if (n.includes("pre-roll") || n.includes("preroll") || n.includes("joint")) return "mdi:cigar";
        if (n.includes("edible") || n.includes("gummy")) return "mdi:cookie-outline";
        if (n.includes("rolling paper") || n.includes("paper")) return "mdi:note-text-outline";
        if (n.includes("tray")) return "mdi:tray";
        if (n.includes("grinder")) return "mdi:cog-outline";
        if (n.includes("bong")) return "mdi:bottle-soda-classic-outline";
        if (n.includes("pipe")) return "mdi:smoking-pipe";
        if (n.includes("lighter") || n.includes("torch")) return "mdi:fire";
        if (n.includes("vape") || n.includes("cart")) return "mdi:battery-charging";
        if (n.includes("cloth") || n.includes("apparel") || n.includes("merch") || n.includes("shirt"))
            return "mdi:tshirt-crew-outline";
        if (n.includes("accessor")) return "mdi:puzzle-outline";
        if (n.includes("drink") || n.includes("beverage")) return "mdi:cup-outline";
        return "mdi:tag-outline";
    };

    const closeDrawer = ()  => {
        if ($app.drawer !== false) {
            $app.drawer = false;
        }
        document.body.style.overflow = '';
    }

    const openDrawer = () => {
        document.body.style.overflow = 'hidden';
    }

    const navigateToCategory = (categoryId) => {
        const base = $app.url.split('?')[0];
        const params = new URLSearchParams($app.url.includes('?') ? $app.url.split('?')[1] : '');
        const cat = categories.find((c) => c.id === categoryId);
        const value = cat ? cat.name : categoryId;
        params.set('category', value);
        const qs = params.toString();
        const target = `/products${qs ? `?${qs}` : ''}`;
        if (!base.startsWith('/products')) {
            push(target);
        } else {
            $app.url = `${base}${qs ? `?${qs}` : ''}`;
        }
        closeDrawer();
    }

    const navigateToAllProducts = () => {
        const base = $app.url.split('?')[0];
        const params = new URLSearchParams($app.url.includes('?') ? $app.url.split('?')[1] : '');
        params.set('category', 'All');
        const qs = params.toString();
        const target = `/products${qs ? `?${qs}` : ''}`;
        if (!base.startsWith('/products')) {
            push(target);
        } else {
            $app.url = `${base}${qs ? `?${qs}` : ''}`;
        }
        closeDrawer();
    }

    $: if ($app.drawer) {
        openDrawer();
    } else {
        document.body.style.overflow = '';
    }

    onDestroy(() => {
        document.body.style.overflow = '';
        if ($app.drawer) {
            $app.drawer = false;
        }
    })

    let internalDrawerState = false;
    $: if ($app.drawer !== internalDrawerState) {
        internalDrawerState = $app.drawer;
    }

    $: {
        const source = $tenant.categories || [];
        categories = source
            .filter((item) => (item.show_on_landing ?? true) && item.name)
            .map((item) => ({
                id: item.name,
                name: item.name,
            }));
    }

    $: lineHandle = $tenant.line || "";
    $: whatsappNumber = $tenant.whatsapp || "";
    $: instagramHandle = $tenant.instagram || "";
    $: addressText = $tenant.address || "";
    $: addressUrl = $tenant.addressLink || "";
</script>

{#if $mq._lg && $app.drawer && isAdmin}
    <!-- New-style admin drawer: blurred backdrop + sliding black/lime panel,
         matching the POS menu drawer. -->
    <div class="cd-backdrop" transition:fade={{ duration: 220 }} on:click={closeDrawer} aria-hidden="true"></div>
    <aside
        class="cd-panel"
        transition:fly={{ x: -480, duration: 400, easing: cubicOut }}
        role="dialog"
        aria-modal="true"
        aria-label="Admin menu"
    >
        <header class="cd-head">
            <h2 class="cd-title">Menu</h2>
            <button class="cd-close" on:click={closeDrawer} aria-label="Close menu">
                <Icon icon="mdi:close" width="22" />
            </button>
        </header>
        <div class="cd-items">
            {#each items as item}
                <a href={item.route} class="cd-row" on:click={closeDrawer}>
                    <div class="cd-thumb">
                        <Icon icon={item.icon} width="28" />
                    </div>
                    <div class="cd-info">
                        <span class="cd-name">{item.name}</span>
                    </div>
                    <Icon icon="fluent:chevron-right-16-regular" width="18" class="cd-chevron" />
                </a>
            {/each}
        </div>
    </aside>
{:else if $mq._lg && $app.drawer}
    <!-- Storefront drawer: same black/lime "cd" panel as the POS + admin menus. -->
    <div class="cd-backdrop" transition:fade={{ duration: 220 }} on:click={closeDrawer} aria-hidden="true"></div>
    <aside
        class="cd-panel"
        transition:fly={{ x: -480, duration: 400, easing: cubicOut }}
        role="dialog"
        aria-modal="true"
        aria-label="Menu"
    >
        <header class="cd-head">
            <h2 class="cd-title">Menu</h2>
            <button class="cd-close" on:click={closeDrawer} aria-label="Close menu">
                <Icon icon="mdi:close" width="22" />
            </button>
        </header>

        <div class="cd-items">
            {#if categories.length}
                <span class="cd-section-label">Categories</span>

                <button class="cd-row" on:click={() => navigateToAllProducts()}>
                    <div class="cd-thumb"><Icon icon="mdi:storefront-outline" width="28" /></div>
                    <div class="cd-info"><span class="cd-name">All products</span></div>
                    <Icon icon="fluent:chevron-right-16-regular" width="18" class="cd-chevron" />
                </button>

                {#each categories as category}
                    <button class="cd-row" on:click={() => navigateToCategory(category.id)}>
                        <div class="cd-thumb"><Icon icon={catIcon(category.name)} width="28" /></div>
                        <div class="cd-info"><span class="cd-name">{category.name}</span></div>
                        <Icon icon="fluent:chevron-right-16-regular" width="18" class="cd-chevron" />
                    </button>
                {/each}
            {/if}

            {#each items as item}
                <a href={item.route} target={isAdmin ? "" : "_blank"} class="cd-row" on:click={closeDrawer}>
                    <div class="cd-thumb"><Icon icon={item.icon} width="28" /></div>
                    <div class="cd-info"><span class="cd-name">{item.name}</span></div>
                    <Icon icon="fluent:chevron-right-16-regular" width="18" class="cd-chevron" />
                </a>
            {/each}

            {#if lineHandle || whatsappNumber || instagramHandle || addressText}
                <span class="cd-section-label">Contact us</span>
                <div class="cd-contact">
                    {#if lineHandle}
                        <div class="cd-contact-row">
                            <Icon icon="mingcute:line-app-line" width="20" />
                            <span>{lineHandle}</span>
                        </div>
                    {/if}
                    {#if whatsappNumber}
                        <div class="cd-contact-row">
                            <Icon icon="ic:baseline-whatsapp" width="20" />
                            <span>{whatsappNumber}</span>
                        </div>
                    {/if}
                    {#if instagramHandle}
                        <div class="cd-contact-row">
                            <Icon icon="mdi:instagram" width="20" />
                            <span>{instagramHandle}</span>
                        </div>
                    {/if}
                    {#if addressText}
                        {#if addressUrl}
                            <a href={addressUrl} target="_blank" rel="noopener noreferrer" class="cd-contact-row">
                                <Icon icon="carbon:location" width="20" />
                                <span>{addressText}</span>
                            </a>
                        {:else}
                            <div class="cd-contact-row">
                                <Icon icon="carbon:location" width="20" />
                                <span>{addressText}</span>
                            </div>
                        {/if}
                    {/if}
                </div>
            {/if}
        </div>
    </aside>
{/if}

<style>
    /* Section label between groups of "cd" rows */
    .cd-section-label {
        display: block;
        padding: 0.4rem 0.15rem 0.1rem;
        font-size: 0.72rem;
        letter-spacing: 0.08em;
        text-transform: uppercase;
        font-weight: 700;
        color: var(--jh-muted);
    }
    .cd-section-label:not(:first-child) { margin-top: 0.4rem; }

    /* Contact card */
    .cd-contact {
        display: flex;
        flex-direction: column;
        gap: 0.85rem;
        padding: 1rem;
        background: var(--jh-surface);
        border: 1px solid var(--jh-line);
        border-radius: var(--jh-radius);
    }
    .cd-contact-row {
        display: flex;
        align-items: center;
        gap: 0.7rem;
        color: var(--jh-text);
        font-size: 0.92rem;
        text-decoration: none;
    }
    .cd-contact-row :global(svg) { color: var(--jh-accent); flex: 0 0 auto; }
    a.cd-contact-row:hover { color: var(--jh-accent); }

    /* ---- Black/lime drawer (mirrors DrawerPOS) ---- */
    .cd-backdrop {
        position: fixed;
        inset: 0;
        z-index: 60;
        background: rgba(5, 6, 4, 0.6);
        backdrop-filter: blur(3px);
        -webkit-backdrop-filter: blur(3px);
    }

    .cd-panel {
        position: fixed;
        top: 0;
        left: 0;
        bottom: 0;
        z-index: 61;
        width: min(420px, 100vw);
        display: flex;
        flex-direction: column;
        background: var(--jh-bg, #0a0a0a);
        border-right: 1px solid var(--jh-line);
        box-shadow: 24px 0 60px rgba(0, 0, 0, 0.55);
        color: var(--jh-text);
        overflow: hidden;
    }

    .cd-head {
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: 1rem;
        padding: 1.15rem 1.3rem;
        border-bottom: 1px solid var(--jh-line);
    }

    .cd-title {
        font-family: var(--jh-font-display);
        font-size: 1.45rem;
        font-weight: 600;
        letter-spacing: -0.01em;
        margin: 0;
    }

    .cd-close {
        display: flex;
        align-items: center;
        justify-content: center;
        width: 38px;
        height: 38px;
        border-radius: 50%;
        border: 1px solid var(--jh-line-strong);
        background: none;
        color: var(--jh-text);
        cursor: pointer;
        transition: border-color 0.2s ease, color 0.2s ease, transform 0.2s ease;
    }

    .cd-close:hover {
        border-color: var(--jh-accent);
        color: var(--jh-accent);
        transform: rotate(90deg);
    }

    .cd-items {
        flex: 1;
        overflow-y: auto;
        padding: 1rem 1.3rem;
        display: flex;
        flex-direction: column;
        gap: 0.8rem;
        overscroll-behavior: contain;
    }

    .cd-row {
        position: relative;
        display: flex;
        align-items: center;
        gap: 0.9rem;
        padding: 0.85rem;
        background: var(--jh-surface);
        border: 1px solid var(--jh-line);
        border-radius: var(--jh-radius);
        color: var(--jh-text);
        text-decoration: none;
        cursor: pointer;
        width: 100%;
        text-align: left;
        transition: border-color 0.2s ease, transform 0.2s ease, background 0.2s ease;
    }

    .cd-row:hover {
        border-color: var(--jh-accent);
        transform: translateX(-2px);
        background: var(--jh-surface);
    }

    .cd-thumb {
        flex: 0 0 48px;
        width: 48px;
        height: 48px;
        border-radius: 12px;
        display: flex;
        align-items: center;
        justify-content: center;
        background: radial-gradient(80% 80% at 50% 30%, #1c1c1c, var(--jh-surface));
        color: var(--jh-accent);
    }

    .cd-info {
        flex: 1;
        min-width: 0;
        display: flex;
        flex-direction: column;
        gap: 0.2rem;
    }

    .cd-name {
        font-weight: 700;
        font-size: 0.98rem;
        line-height: 1.25;
    }

    :global(.cd-row .cd-chevron) {
        color: var(--jh-muted);
        flex: 0 0 auto;
    }
</style>
